import React, { useMemo } from "react";
import { addYears, isValid, parse, startOfDay } from "date-fns";
import { SaleStep } from "../../store/stepSaleConfig/stepSaleConfigReducer";
import SwimmingBar from "./SwimmingBar";
import SwimmingGrid from "./SwimmingGrid";
import SwimmingWeeklyGrid from "./SwimmingWeeklyGrid";
import SwimmingLessonModal from "./SwimmingLessonModal";
import useSwimmingLessons from "./useSwimmingLessons";
import useSwimmingData from "./useSwimmingData";
import useSwimmingFirstTime from "./useSwimmingFirstTime";
import useSwimmingDateNavigation from "./useSwimmingDateNavigation";
import "./style.scss";

interface IProps {
  saleStep: SaleStep;
}

const parseSaleStepDate = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? startOfDay(parsed) : undefined;
};

const SwimmingLessons: React.FC<IProps> = ({ saleStep }) => {
  const isWeekly = saleStep.weeklyGridScheduleVariant === true;
  const minDate = useMemo(() => parseSaleStepDate(saleStep.dateFromValue), [saleStep.dateFromValue]);
  const maxDate = useMemo(() => parseSaleStepDate(saleStep.dateToValue), [saleStep.dateToValue]);
  const effectiveMaxDate = useMemo(() => maxDate ?? addYears(new Date(), 1), [maxDate]);

  const {
    isMobile,
    anchorDate,
    dateFrom,
    dateTo,
    canGoPrevious,
    canGoNext,
    handlePreviousPeriod,
    handleNextPeriod,
    handleDateChange,
    dateHeading,
    dateRangeLabel,
  } = useSwimmingDateNavigation({ isWeekly, minDate, maxDate });

  const { data, loading } = useSwimmingData({
    dateFrom,
    dateTo,
    exerciseGroupIds: saleStep.exerciseGroupIds,
  });

  const {
    filters,
    setFilters,
    selectedLesson,
    modalOpen,
    handleLessonClick,
    handleCloseModal,
    handleClearFilters,
    handleDateChange: handleLessonsDateChange,
    setShowPastHours,
    visibleHours,
    hasPastHours,
    filteredLessons,
    activeColumns,
    findLessonsForSlot,
    isDayColumnInRange,
    locations,
    instructors,
    exerciseGroups,
  } = useSwimmingLessons({
    data,
    anchorDate,
    onDateChange: handleDateChange,
    isWeekly,
    isMobile,
    minDate,
    maxDate,
    defaultCourseType: saleStep.academyDefaultCourseType,
    defaultExerciseGroup: saleStep.academyDefaultExerciseGroup,
    defaultInstructor: saleStep.academyDefaultInstructor,
    defaultGroupingVariant: saleStep.academyDefaultGroupingVariant,
  });

  const isGridEmpty = !loading && (isWeekly ? filteredLessons.length === 0 : activeColumns.length === 0);

  const {
    firstAvailableDate,
    loading: firstTimeLoading,
    checked: firstTimeChecked,
  } = useSwimmingFirstTime({
    enabled: isGridEmpty,
    filters,
    saleStepExerciseGroupIds: saleStep.exerciseGroupIds,
    minDate,
    maxDate: effectiveMaxDate,
  });

  const handleGoToNearestDate = () => {
    if (firstAvailableDate) {
      handleLessonsDateChange(firstAvailableDate);
    }
  };

  const onDateChange = (date: Date) => {
    handleLessonsDateChange(date);
  };

  return (
    <div className="swimming-lessons">
      <SwimmingBar
        selectedDate={anchorDate}
        onDateChange={onDateChange}
        filters={filters}
        onFiltersChange={setFilters}
        onClearFilters={handleClearFilters}
        availableLocations={locations}
        availableInstructors={instructors}
        availableExerciseGroups={exerciseGroups}
        hideFilters={saleStep.hideFilters}
        minDate={minDate}
        maxDate={maxDate}
        isWeekly={isWeekly}
        onPreviousPeriod={handlePreviousPeriod}
        onNextPeriod={handleNextPeriod}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        dateRangeLabel={dateRangeLabel}
      />

      <div className="swimming-lessons__date-heading">
        <h5 className="swimming-lessons__date-heading-text">{dateHeading}</h5>
      </div>

      {isWeekly ? (
        <SwimmingWeeklyGrid
          columns={activeColumns}
          visibleHours={visibleHours}
          hasPastHours={hasPastHours}
          onShowPastHours={() => setShowPastHours(true)}
          onLessonClick={handleLessonClick}
          findLessonsForSlot={findLessonsForSlot}
          isDayColumnInRange={isDayColumnInRange}
          loading={loading}
          isEmpty={isGridEmpty}
          firstAvailableDate={firstAvailableDate}
          firstTimeLoading={firstTimeLoading}
          firstTimeChecked={firstTimeChecked}
          onGoToNearestDate={handleGoToNearestDate}
        />
      ) : (
        <SwimmingGrid
          columns={activeColumns}
          lessons={filteredLessons}
          visibleHours={visibleHours}
          hasPastHours={hasPastHours}
          onShowPastHours={() => setShowPastHours(true)}
          onLessonClick={handleLessonClick}
          loading={loading}
          groupingVariant={filters.groupingVariant}
          instructors={instructors}
          firstAvailableDate={firstAvailableDate}
          firstTimeLoading={firstTimeLoading}
          firstTimeChecked={firstTimeChecked}
          onGoToNearestDate={handleGoToNearestDate}
        />
      )}

      <SwimmingLessonModal lesson={selectedLesson} isOpen={modalOpen} onClose={handleCloseModal} />
    </div>
  );
};

export default SwimmingLessons;
